import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Affiliate LifeWave Shop Link
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';

const ProductCard = ({ product }) => {
  const { t, language, translateContent } = useLanguage();
  const [translatedDesc, setTranslatedDesc] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  
  useEffect(() => {
    const translateDescription = async () => {
      const roDesc = product.descriptions?.ro;
      const enDesc = product.descriptions?.en || product.description || '';
      
      // ROMANIAN
      if (language === 'ro') {
        if (roDesc) {
          setTranslatedDesc(roDesc);
        } else if (enDesc) {
          setIsTranslating(true);
          try {
            const translated = await translateContent(enDesc, 'product', `${product.id}_card_ro`);
            setTranslatedDesc(translated);
          } catch (e) {
            setTranslatedDesc(enDesc);
          } finally {
            setIsTranslating(false);
          }
        }
        return;
      }
      
      // ENGLISH
      if (language === 'en') {
        setTranslatedDesc(enDesc || roDesc || '');
        return;
      }
      
      // OTHER (German, etc.) - translate with AI
      const sourceText = roDesc || enDesc;
      if (sourceText) {
        setIsTranslating(true);
        try {
          const translated = await translateContent(sourceText, 'product', `${product.id}_card_${language}`);
          setTranslatedDesc(translated);
        } catch (e) {
          setTranslatedDesc(sourceText);
        } finally {
          setIsTranslating(false);
        }
      }
    };

    translateDescription();
  }, [product, language, translateContent]);
  
  const handleBuyNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Use product-specific affiliate link if available, otherwise use default store
    const buyUrl = product.affiliate_link || LIFEWAVE_SHOP_URL;
    
    // Open link FIRST (must be synchronous with user click to avoid popup blocker)
    window.open(buyUrl, '_blank');
    
    // Track the click (non-blocking)
    axios.post(`${API_URL}/api/track/affiliate-click`, {
      product_id: product.id,
      product_name: product.name,
      affiliate_link: buyUrl
    }).catch(error => console.error('Error tracking click:', error));
  };

  const qvPoints = product.qv_points || product.pv_points || 0;

  return (
    <Link 
      to={`/products/${product.id}`}
      className="group relative bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      {/* QV Badge */}
      <div className="absolute top-4 right-4 bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full">
        {qvPoints} QV
      </div>

      {/* Product Image */}
      <div className="aspect-square mb-4 rounded-xl overflow-hidden bg-slate-50">
        <img 
          src={product.image_url} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">
          {product.category}
        </p>
        <h3 className="font-bold text-slate-900 group-hover:text-sky-600 transition-colors line-clamp-2">
          {product.name}
        </h3>
        {isTranslating ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
            <span className="text-slate-400 text-sm">...</span>
          </div>
        ) : (
          <p className="text-slate-500 text-sm line-clamp-2">
            {translatedDesc}
          </p>
        )}
        
        <div className="flex items-center justify-between pt-4">
          <div>
            <span className="text-2xl font-bold text-slate-900">
              ${product.price.toFixed(2)}
            </span>
            <p className="text-xs text-slate-400">{t('priceExcl')}</p>
          </div>
          <Button 
            size="sm"
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-full"
            onClick={handleBuyNow}
            data-testid={`buy-lifewave-${product.id}`}
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            {t('buyNow')}
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
